import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://kenluk-backend-production.up.railway.app/api';

class WalletService {
    constructor() {
        this.api = axios.create({
            baseURL: API_BASE_URL,
            headers: {
                'Content-Type': 'application/json'
            },
            withCredentials: true
        });

        // Add token to requests
        this.api.interceptors.request.use(config => {
            const token = sessionStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });
    }

    /**
     * Initialize wallet for user
     */
    async initializeWallet() {
        try {
            const response = await this.api.get('/wallet/initialize');
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to initialize wallet');
        }
    }

    /**
     * Get wallet balance
     */
    async getBalance() {
        try {
            const response = await this.api.get('/wallet/balance');
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get balance');
        }
    }

    /**
     * Get wallet summary
     */
    async getSummary() {
        try {
            const response = await this.api.get('/wallet/summary');
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get wallet summary');
        }
    }

    /**
     * Get transaction history
     */
    async getTransactionHistory(page = 1, limit = 10) {
        try {
            const response = await this.api.get('/wallet/transactions', {
                params: { page, limit }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get transaction history');
        }
    }

    /**
     * Get funding history
     */
    async getFundingHistory(page = 1, limit = 10) {
        try {
            const response = await this.api.get('/wallet/funding-history', {
                params: { page, limit }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get funding history');
        }
    }

    /**
     * Validate if user has sufficient balance
     */
    async validateBalance(amount) {
        try {
            const response = await this.api.post('/wallet/validate-balance', { amount });
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to validate balance');
        }
    }

    /**
     * Process Fidelity payment completion for wallet funding
     */
    async processFidelityPayment(fidelityPaymentId) {
        try {
            const response = await this.api.post('/wallet/process-fidelity-payment', {
                fidelityPaymentId
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to process Fidelity payment');
        }
    }

    /**
     * Check if payment amount exceeds balance
     */
    async canSubmitPayment(amount) {
        try {
            const validation = await this.validateBalance(amount);
            return validation.isValid;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get error message for insufficient balance
     */
    async getInsufficientBalanceMessage(amount) {
        try {
            const validation = await this.validateBalance(amount);
            if (!validation.isValid) {
                return validation.message;
            }
            return '';
        } catch (error) {
            return error.message;
        }
    }
}

const walletService = new WalletService();
export default walletService;
